import React, { useContext, useRef, useState } from "react";
import { cls } from "cls";
import { FieldErrors, SubmitHandler, UseFormReturn } from "react-hook-form";
import { Modal } from "@clarkinc/zest-design-system";
import { z } from "zod";
import { SectionGroup } from "../../Filters";
import {
  getNewFilterRangeUrl,
  filterRangeValidationSchema,
  getShortenUnits,
  unitValueRequiredMessage,
} from "../../FilterRangeHelper";
import {
  filterRangeInputStyles,
  filterRangeInputContainerStyles,
  filterRangeInputLabelStyles,
  filterRangeInputInlineStyles,
} from "../../FilterRangeStyles";
import Form from "~/common/Form";
import Input from "~/common/Form/Input";
import SelectInput from "~/common/Form/SelectInput";
import InputError from "~/common/Form/InputError";
import Button from "~/common/Buttons";
import ConnectForm from "~/common/Form/ConnectForm";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import { FilterModalContext } from "../../../FilterModalContextProvider";

interface FilterRangeProps {
  units?: string[];
  selectedMinValue?: string;
  selectedMaxValue?: string;
  selectedUnit?: string;
  pageUrl?: string;
  group: SectionGroup;
  clearCustomFilterRangeUrl: () => void;
}

type FormFields = {
  min: string;
  max: string;
  units?: string;
};

export default function FilterRange({
  units = [],
  clearCustomFilterRangeUrl,
  selectedMaxValue = "",
  selectedMinValue = "",
  selectedUnit = "",
  pageUrl = "",
  group,
}: FilterRangeProps) {
  const [customFilterRangeErrors, setCustomFilterRangeErrors] = useState({
    errorLabel: "",
    errorName: "",
  });
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  const formMethodsRef = useRef<UseFormReturn<FormFields>>();

  const showSelect = Boolean(units && units.length > 1);

  const defaultUnitValue =
    !selectedUnit && !showSelect && !!units ? units[0] : selectedUnit;

  const { compareProductsActive, storedFilterUrl } =
    useContext(FilterModalContext);

  const shouldOpenModal = compareProductsActive && storedFilterUrl;

  const handleSubmit: SubmitHandler<FormFields> = async (data) => {
    const { min = "", max = "", units = "" } = data;
    const newFilterRangeUrl = getNewFilterRangeUrl(
      group,
      pageUrl,
      min,
      max,
      !units ? defaultUnitValue : units,
    );
    if (shouldOpenModal) {
      Modal.open("confirm-edit-filters-warning");

      storedFilterUrl.current.url = newFilterRangeUrl; // TODO: test this - we should not be reassigning state this way - chances are we don't need this line
    } else {
      setWindowLocationHref(newFilterRangeUrl);
    }
  };

  const clearRange = () => clearCustomFilterRangeUrl();

  const onError = (
    errors: FieldErrors<
      z.infer<ReturnType<typeof filterRangeValidationSchema>>
    >,
  ) => {
    if (!formMethodsRef.current) return;
    const messages: string[] = [];
    let unitError = ""; // Store "Unit is required" separately
    for (const [key, error] of Object.entries(errors)) {
      if (key === "custom") continue;
      if (!error.message) continue;
      if (!(typeof error.message === "string")) continue;
      const trimmedMessage = error.message.trim();
      // skip duplicate messages
      if (messages.includes(trimmedMessage)) continue;
      // If it's the unit error, store it separately
      if (
        trimmedMessage
          .toLowerCase()
          .includes(unitValueRequiredMessage.toLowerCase())
      ) {
        unitError = trimmedMessage;
        continue;
      }
      messages.push(trimmedMessage);
    }
    if (!messages.length && !unitError) {
      if (!("custom" in errors)) return;
      return setCustomFilterRangeErrors({
        errorLabel: "custom",
        errorName: "custom",
      });
    }
    const last = (unitError || messages.pop()) ?? "";
    let message = messages.join(", ").trim();
    if (messages.length > 0 && messages.length < 2) {
      message += " and ";
    } else if (messages.length >= 2) {
      message += ", and ";
    }
    message += last;
    formMethodsRef.current.setError("root", { message });
    setCustomFilterRangeErrors({
      errorLabel: "root",
      errorName: "root",
    });
  };

  return (
    <li className="list-none">
      <Form
        onSubmit={handleSubmit}
        formMethodsRef={formMethodsRef}
        validationSchema={filterRangeValidationSchema(
          showSelect,
          defaultUnitValue,
          group,
          pageUrl,
        )}
        onValidationFailed={onError}
        validationMode="onSubmit"
        reValidateMode="onSubmit"
      >
        <ConnectForm>
          {({ errors }) => {
            return (
              <div className={cls("pr-3.5", "lt:mt-1 lt:mx-2", "lt:pr-0")}>
                <div className={cls("flex", "justify-center", "items-center")}>
                  <Input
                    id={`${group.groupName}-min`}
                    containerClassName={filterRangeInputContainerStyles(
                      showSelect,
                    )}
                    label="Min"
                    labelClassName={filterRangeInputLabelStyles}
                    type="text"
                    inputErrorStyles={filterRangeInputInlineStyles}
                    overrideDefaultInputHeight
                    className={filterRangeInputStyles(showSelect)}
                    defaultValues={selectedMinValue}
                    name="min"
                    data-testid="minFilterInput"
                    displayErrorMessage={false}
                    clearErrorOnFocus={false}
                    innerRef={minRef}
                    aria-label="min"
                    aria-describedby={`${group.groupName}-errors`}
                  />
                  <span
                    className={cls(
                      "inline-block text-sm relative mx-1 text-gray-800 mt-3.5 mb-2", // => base/smallest screensize
                      "lt:text-xs lt:mt-4",
                    )}
                  >
                    to
                  </span>
                  <Input
                    id={`${group.groupName}-max`}
                    containerClassName={filterRangeInputContainerStyles(
                      showSelect,
                    )}
                    label="Max"
                    labelClassName={filterRangeInputLabelStyles}
                    type="text"
                    overrideDefaultInputHeight
                    className={filterRangeInputStyles(showSelect)}
                    inputErrorStyles={filterRangeInputInlineStyles}
                    defaultValues={selectedMaxValue}
                    data-testid="maxFilterInput"
                    clearErrorOnFocus={false}
                    name="max"
                    displayErrorMessage={false}
                    innerRef={maxRef}
                    aria-label="max"
                    aria-describedby={`${group.groupName}-errors`}
                  />
                  {showSelect ? (
                    <div className="self-end">
                      <SelectInput
                        label="Units"
                        labelClassName={cls("offscreen")}
                        containerClassName={cls(
                          "ml-2",
                          "pb-1.5",
                          "h-10",
                          "lt:h-6",
                          "lt:w-[54px]",
                          "min-w-[112px]",
                          "lt:min-w-0",
                        )}
                        selectClassName={cls(
                          "py-0",
                          "border-gray-400",
                          "text-gray-800",
                          "appearance-none",
                          "focus:bg-white",
                          "rounded",
                          "border-solid",
                          "border",
                          "box-border",
                          "text-sm",
                          "lt:text-xs",
                          "m-0",
                          "focus:placeholder-transparent",
                          "w-full",
                          "top-0",
                          "cursor-pointer",
                          "whitespace-nowrap",
                          "text-ellipsis",
                          "bg-none",
                          "pl-4",
                          "form-input",
                          "lt:pl-1",
                        )}
                        placeHolderClassName={cls(
                          "[&:not(:placeholder-shown)]:pr-[26px]",
                          "lt:[&:not(:placeholder-shown)]:pr-3",
                        )}
                        style={{
                          height: "100%",
                          background: "transparent",
                        }}
                        placeholder="unit"
                        options={units.map((unit) => ({
                          value: unit,
                          text: getShortenUnits(unit),
                        }))}
                        selectErrorStyles={{
                          background: "transparent",
                          border: "1px solid #CF3700",
                          boxShadow: "0px 0px 0px 2px #FED7D7",
                        }}
                        hiddenLabel
                        iconStyle={{
                          width: 14,
                          height: 13,
                          color: "#3B3D3B",
                        }}
                        name="units"
                        svgClassName={cls(
                          "mt-[14px]",
                          "mr-4",
                          "lt:mt-[5px]",
                          "lt:mr-0.5",
                        )}
                        displayErrorMessage={false}
                        defaultValue={selectedUnit}
                        dataTestId="unitFilterOption"
                      />
                    </div>
                  ) : null}
                </div>
                <InputError
                  id={`${group.groupName}-errors`}
                  errors={errors}
                  customErrorMessage
                  label={customFilterRangeErrors.errorLabel}
                  name={customFilterRangeErrors.errorName}
                  className={cls(
                    "relative",
                    "pb-1.5",
                    "block",
                    "font-semibold",
                    "text-sm",
                    "lt:font-normal",
                    "lt:text-2xs",
                  )}
                  styles={{ bottom: 0, color: "#CF3700" }}
                />
                <div
                  className={cls(
                    "flex",
                    "justify-end",
                    "relative",
                    "mt-0",
                    "mb-2",
                  )}
                >
                  <Button
                    size="custom"
                    className={cls(
                      "rounded",
                      "w-full",
                      "text-sm",
                      "box-border",
                      "bg-gray-100",
                      "text-green-600",
                      "border-none",
                      "h-10",
                      "lt:h-6",
                      "lt:my-0",
                      "lt:py-0.5",
                      "lt:px-2",
                      "lt:text-xs",
                      "[@media(hover:hover)]:hover:bg-gray-200",
                      "active:bg-gray-200",
                      "active:shadow-[inset_0_4px_2px_0_rgba(0,0,0,0.15)]",
                    )}
                    color="custom"
                    type="submit"
                    data-testid="applyFilterBtn"
                  >
                    Apply
                  </Button>
                  {selectedMinValue && selectedMaxValue && (
                    <button
                      className={cls(
                        "bg-transparent",
                        "border-none",
                        "text-green-500",
                        "text-sm",
                        "underline",
                        "font-semibold",
                        "px-0",
                        "mx-2.5",
                        "lt:text-xs",
                        "min-w-[100px]",
                        "lt:min-w-[36px]",
                        "lt:min-w-[44px]",
                        "hover:text-green-700",
                        "active:text-green-800",
                      )}
                      type="button"
                      onClick={clearRange}
                      data-testid="clear-range"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            );
          }}
        </ConnectForm>
      </Form>
    </li>
  );
}
